import SimulationApi from '@/modules/calculator-v25/services/shared/SimulationApi';
import type {
  CashAddOffersToDealRequest,
  CashCreateSimulationRequest,
  CashCreateSimulationResponse,
  CashDuplicateLoanRequest,
  CashDuplicateLoanResponse,
  CashGetOffersByIdsRequest,
  CashGetOffersByIdsResponse,
  CashGetScheduleRequest,
  CashGetScheduleResponse,
  CashGetSimulationByIdResponse,
  CashIsLoanAvailableRequest,
  CashIsLoanAvailableResponse,
  CashPinOfferResponse,
  CashRemoveDuplicateRequest,
  CashRemoveDuplicateResponse,
  CashSelectOfferResponse,
  CashUnpinOfferResponse,
  CashUnselectOfferResponse,
  CashUpdateSimulationByIdRequest,
  CashUpdateSimulationByIdResponse,
} from '@/modules/calculator-v25/types/cash/dto/endpoints';

class CashSimulationApi {
  private client = SimulationApi.client;

  private get apiUrl(): string {
    return `${SimulationApi.baseUrl}cash-backend/api`;
  }

  public async createSimulation(request: CashCreateSimulationRequest): Promise<CashCreateSimulationResponse> {
    const response = await this.client.post<CashCreateSimulationResponse>(
      `${this.apiUrl}/simulation`,
      SimulationApi.withoutEmptyHouseholds(request),
    );
    return response.data;
  }

  public async getSimulationById(simulationId: string): Promise<CashGetSimulationByIdResponse> {
    const response = await this.client.get<CashGetSimulationByIdResponse>(`${this.apiUrl}/simulation/${simulationId}`);
    return response.data;
  }

  public async updateSimulationById(simulationId: string, request: CashUpdateSimulationByIdRequest): Promise<CashUpdateSimulationByIdResponse> {
    const response = await this.client.post<CashUpdateSimulationByIdResponse>(
      `${this.apiUrl}/simulation/${simulationId}`,
      SimulationApi.withoutEmptyHouseholds(request),
    );
    return response.data;
  }

  public async isLoanAvailable(loanId: string, request: CashIsLoanAvailableRequest): Promise<CashIsLoanAvailableResponse> {
    const response = await this.client.post<CashIsLoanAvailableResponse>(
      `${this.apiUrl}/simulation/loan/${loanId}/isAvailable`,
      SimulationApi.withoutEmptyHouseholds(request),
    );
    return response.data;
  }

  public async duplicateLoan(loanId: string, request: CashDuplicateLoanRequest): Promise<CashDuplicateLoanResponse> {
    const response = await this.client.post<CashDuplicateLoanResponse>(`${this.apiUrl}/simulation/loan/${loanId}/duplicate`, request);
    return response.data;
  }

  public async removeDuplicate(loanId: string, request: CashRemoveDuplicateRequest): Promise<CashRemoveDuplicateResponse> {
    const response = await this.client.post<CashRemoveDuplicateResponse>(`${this.apiUrl}/simulation/loan/${loanId}/removeDuplicate`, request);
    return response.data;
  }

  public async getOffersByIds(simulationId: string, request: CashGetOffersByIdsRequest): Promise<CashGetOffersByIdsResponse> {
    const response = await this.client.post<CashGetOffersByIdsResponse>(`${this.apiUrl}/simulation/${simulationId}/offers`, request);
    return response.data;
  }

  public async pinOffer(simulationId: string, offerId: string): Promise<CashPinOfferResponse> {
    const response = await this.client.put<CashPinOfferResponse>(`${this.apiUrl}/simulation/${simulationId}/pinned-offers/${offerId}`);
    return response.data;
  }

  public async unpinOffer(simulationId: string, offerId: string): Promise<CashUnpinOfferResponse> {
    const response = await this.client.delete(`${this.apiUrl}/simulation/${simulationId}/pinned-offers/${offerId}`);
    return response.data;
  }

  public async selectOffer(simulationId: string, offerId: string): Promise<CashSelectOfferResponse> {
    const response = await this.client.put<CashSelectOfferResponse>(`${this.apiUrl}/simulation/${simulationId}/selected-offers/${offerId}`);
    return response.data;
  }

  public async unselectOffer(simulationId: string, offerId: string): Promise<CashUnselectOfferResponse> {
    const response = await this.client.delete(`${this.apiUrl}/simulation/${simulationId}/selected-offers/${offerId}`);
    return response.data;
  }

  public async getSchedule(request: CashGetScheduleRequest): Promise<CashGetScheduleResponse> {
    const response = await this.client.post<CashGetScheduleResponse>(`${this.apiUrl}/schedule`, request);
    return response.data;
  }

  public addOffersToDeal(dealId: number, request: CashAddOffersToDealRequest): Promise<void> {
    return SimulationApi.addOffersToDeal(dealId, request.simulationId, request.simulationInput, request.offers);
  }
}

export default new CashSimulationApi();
